/*
 * The release-note editor (S42, 2026-10-12).
 *
 * The owner's request: release notes are handled in the admin site "similar to
 * Articles" - product tag selection, publishing options, text editing with
 * Write/Split/Preview, inline images, links to articles, change history. So this
 * page mirrors ArticleEditorPage panel for panel, and the split between panels
 * follows the columns they write, for the same reasons that page gives:
 *
 *   Properties     kb.ReleaseNote - product and version, the note's ADDRESS
 *                  (/releasenotes/<productSlug>/<version>). Under the row's
 *                  rowversion; a pair another note already has is a 409 shown
 *                  under the version (ReleaseNoteProperties).
 *   Publishing     kb.ReleaseNote - IsVisible and PublishAtUtc; the article's
 *                  PublishControls, handed this row and this row's call.
 *   Release note   kb.ReleaseNoteRevision - the change note and the two saves
 *                  (Save & publish, Save as draft); the article's ArticleSaveBox.
 *   Text           kb.ReleaseNoteRevision - the single-line title (at most 200,
 *                  the column's NVARCHAR(200)) and the body, with the images the
 *                  body refers to. Append-only, guarded by the base revision
 *                  number.
 *
 * The shared editor components are the article ones behind small adapters rather
 * than copies (imageOwner.ts, RevisionHistoryPanel's revision source,
 * PublishControls' entity form). What differs here, deliberately:
 *
 *   - No video embeds: the owner's answer. The editor hides "Insert video" and
 *     its preview leaves a video token as the literal text a reader would see.
 *   - The article picker excludes nothing: a release note is not an article.
 *   - No Linked-from card and no rename rewriting: nothing links TO a release
 *     note, so moving one has nothing to rewrite (decision D-g).
 *   - "View on site" goes to the note's own page, /releasenotes/<product>/<version>.
 *
 * The body is keyed on the note's id and re-seeds on the latest revision number,
 * for exactly the reason ArticleEditorPage spells out at its call site: a draft
 * must never survive into a different note.
 *
 * Its words come from the chosen language's dictionary, `t.admin.releaseNotes` (S48).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { AdminReleaseNoteDetail, ArticleImage, DeleteResult } from '@accotool-kb/shared';
import {
  ADMIN_RELEASE_NOTES_PATH,
  RELEASE_NOTE_TITLE_MAX_LENGTH,
  isSingleLineTitle,
  releaseNoteHref,
  releaseNoteLabel,
} from '@accotool-kb/shared';
import { useAdminApi } from '../auth/AuthProvider.js';
import { useAction, useAsync } from '../hooks/useAsync.js';
import { DateTime, ErrorBanner, Field, Loading, PublishingStatusBadge } from '../components/ui.js';
import type { MetadataBundle } from '../api/adminApi.js';
import { ApiError } from '../api/client.js';
import { ReleaseNoteProperties } from './components/ReleaseNoteProperties.js';
import { PublishControls } from './components/PublishControls.js';
import { ArticleSaveBox } from './components/ArticleSaveBox.js';
import { MarkdownEditor, type MarkdownEditorHandle } from './components/MarkdownEditor.js';
import { ArticleImagesPanel } from './components/ArticleImagesPanel.js';
import { ArticleImagePreviewsProvider } from './components/ArticleImagePreviewsProvider.js';
import {
  RevisionHistoryPanel,
  releaseNoteRevisionSource,
} from './components/RevisionHistoryPanel.js';
import { DeleteImpactDialog, type DeleteTarget } from './components/DeleteImpactDialog.js';
import { releaseNoteImageOwner } from './components/imageOwner.js';
import { useMessages } from '../i18n/language.js';
import { richText } from '../i18n/richText.js';
import { releaseNotesEn } from '../i18n/admin/releaseNotes.en.js';

/** What every sentence on this page calls the thing being edited. */
const NOUN = 'release note';

/**
 * The title rule, client-side, in the words releaseNoteTitleSchema answers with -
 * in the language of `words`, English unless told otherwise.
 */
export function releaseNoteTitleError(
  title: string,
  words: typeof releaseNotesEn = releaseNotesEn,
): string | undefined {
  if (title.trim().length === 0) return words.common.titleRequired;
  if (!isSingleLineTitle(title)) return words.common.titleSingleLine;
  return undefined;
}

/** /admin/releasenotes/:releaseNoteId - one release note: properties, publishing, text, history. */
export function ReleaseNoteEditorPage(): JSX.Element {
  const words = useMessages().admin.releaseNotes;
  const api = useAdminApi();
  const params = useParams();
  const releaseNoteId = params.releaseNoteId ?? '';

  const state = useAsync(() => api.getReleaseNote(releaseNoteId), [releaseNoteId]);
  const metadata = useAsync(() => api.metadata(), []);

  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deletedNotice, setDeletedNotice] = useState<DeleteResult | null>(null);
  const [historyKey, setHistoryKey] = useState(0);

  // The history's four calls, built once per note. The panel keys on the
  // source's key, so a rebuilt adapter would not refetch anyway; the memo keeps
  // that from depending on it.
  const revisions = useMemo(
    () => releaseNoteRevisionSource(api, releaseNoteId),
    [api, releaseNoteId],
  );

  const note = state.data;

  if (state.loading && !note) return <Loading label={words.editor.loading} />;
  if (state.error) return <ErrorBanner error={state.error} onRetry={state.reload} />;
  if (!note) return <Loading />;

  // The note was deleted from this page: there is nothing left to edit.
  if (deletedNotice) {
    return (
      <div className="card">
        <h1>{words.editor.deleted}</h1>
        <p>{words.editor.deletedEverything(deletedNotice.targetLabel)}</p>
        <ul>
          {deletedNotice.performed.map((step) => (
            <li key={step.code}>{step.description}</li>
          ))}
        </ul>
        <Link className="button button-primary" to={ADMIN_RELEASE_NOTES_PATH}>
          {words.editor.backToList}
        </Link>
      </div>
    );
  }

  const heading = note.title ?? words.common.untitled;

  return (
    <>
      <div className="page-header">
        <div>
          <nav className="breadcrumb">
            <Link to={ADMIN_RELEASE_NOTES_PATH}>{words.common.releaseNotes}</Link>
          </nav>
          <h1>{heading}</h1>
          <p className="faint">
            {richText(words.editor.byline, {
              product: note.product.name,
              version: <code>{note.version}</code>,
              created: <DateTime value={note.createdAtUtc} />,
              modified: <DateTime value={note.modifiedAtUtc} />,
            })}
          </p>
        </div>

        <div className="button-row">
          <PublishingStatusBadge status={note.status} noun={NOUN} />
          {note.status === 'live' && (
            <a
              className="button button-small"
              href={releaseNoteHref(note.product.slug, note.version)}
              target="_blank"
              rel="noreferrer"
            >
              {words.editor.viewOnSite}
            </a>
          )}
          <button
            type="button"
            className="button button-small button-quiet"
            onClick={() =>
              setDeleteTarget({
                kind: 'release-note',
                targetId: note.releaseNoteId,
                // The label the server's impact report uses too (A14), so the
                // typed confirmation is the same string at both ends.
                label: releaseNoteLabel(note.product.name, note.version),
              })
            }
          >
            {words.editor.deleteReleaseNote}
          </button>
        </div>
      </div>

      {/* Keyed on the id: see ArticleEditorPage's comment at the same place. */}
      <ReleaseNoteBody
        key={note.releaseNoteId}
        note={note}
        metadata={metadata.data}
        onUpdated={state.setData}
        onRevisionsChanged={() => setHistoryKey((key) => key + 1)}
      />

      <div className="card">
        <RevisionHistoryPanel
          revisions={revisions}
          currentRevisionNumber={note.currentRevisionNumber}
          onChanged={() => void api.getReleaseNote(note.releaseNoteId).then(state.setData)}
          reloadKey={historyKey}
        />
      </div>

      <DeleteImpactDialog
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={(result) => {
          setDeletedNotice(result);
          setDeleteTarget(null);
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// One new revision: the title and body being typed, the change note, and the
// two saves - split across two cards but one unit of work, as on the article
// editor, which is why the state lives here
// ---------------------------------------------------------------------------

function ReleaseNoteBody({
  note,
  metadata,
  onUpdated,
  onRevisionsChanged,
}: {
  note: AdminReleaseNoteDetail;
  metadata: MetadataBundle | null;
  onUpdated: (next: AdminReleaseNoteDetail) => void;
  onRevisionsChanged: () => void;
}): JSX.Element {
  const t = useMessages();
  const words = t.admin.releaseNotes;
  const api = useAdminApi();
  const save = useAction();

  const latest = note.latestRevision;

  const [title, setTitle] = useState(latest?.title ?? '');
  const [content, setContent] = useState(latest?.content ?? '');
  const [changeNote, setChangeNote] = useState('');
  // What was saved rather than the sentence, so the notice follows the language.
  const [savedNotice, setSavedNotice] = useState<{
    revisionNumber: number;
    published: boolean;
    readersSee: number | null;
  } | null>(null);

  // Re-seed when the server hands back a newer revision; keyed on its number so
  // a re-render never discards what is being typed.
  useEffect(() => {
    setTitle(latest?.title ?? '');
    setContent(latest?.content ?? '');
    setChangeNote('');
  }, [latest?.revisionNumber]);

  const dirty = useMemo(
    () => title !== (latest?.title ?? '') || content !== (latest?.content ?? ''),
    [title, content, latest],
  );

  const titleError = releaseNoteTitleError(title, words);

  // Warn before losing an unsaved edit, as the article editor does.
  useEffect(() => {
    if (!dirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const saveRevision = async (publish: boolean) => {
    if (titleError) return;

    const previousCurrent = note.currentRevisionNumber;

    const result = await save.run(() =>
      api.saveReleaseNoteRevision(note.releaseNoteId, {
        title: title.trim(),
        content,
        changeNote: changeNote.trim() || null,
        publish,
        // The revision this editor opened: a save stacked on somebody else's
        // newer one is refused rather than made.
        baseRevisionNumber: note.latestRevisionNumber ?? 0,
      }),
    );

    if (result) {
      onUpdated(result.releaseNote);
      onRevisionsChanged();
      setSavedNotice({
        revisionNumber: result.revision.revisionNumber,
        published: publish,
        readersSee: previousCurrent,
      });
    }
  };

  return (
    <>
      <div className="split-two" style={{ marginBottom: '1.5rem' }}>
        <ReleaseNoteProperties note={note} metadata={metadata} onUpdated={onUpdated} />

        {/* Two stacked cards in one grid cell - see ArticleEditorPage. */}
        <div>
          <PublishControls
            entity={note}
            noun={NOUN}
            onAction={async (body) => {
              const next = await api.releaseNotePublishAction(note.releaseNoteId, body);
              onUpdated(next);
              return next;
            }}
          />
          <ArticleSaveBox
            heading={words.editor.saveBoxHeading}
            placeholder={words.editor.changeNotePlaceholder}
            changeNote={changeNote}
            onChangeNote={setChangeNote}
            onSave={saveRevision}
            saving={save.running}
            blocked={Boolean(titleError)}
            dirty={dirty}
            latest={latest}
          />
        </div>
      </div>

      {savedNotice && (
        <div className="notice notice-success" role="status">
          <div className="notice-body">
            {savedNotice.published
              ? words.editor.savedAndPublished(savedNotice.revisionNumber)
              : words.editor.savedAsDraft(savedNotice.revisionNumber, savedNotice.readersSee)}
          </div>
          <div className="notice-actions">
            <button
              type="button"
              className="button button-quiet"
              onClick={() => setSavedNotice(null)}
            >
              {t.common.dismiss}
            </button>
          </div>
        </div>
      )}

      {save.error && <ErrorBanner error={save.error} onDismiss={save.clearError} />}

      {save.error instanceof ApiError && save.error.isConcurrencyConflict && (
        <div className="notice notice-warning">
          <div className="notice-body">
            <strong>{words.editor.outOfDate}</strong>
            <p style={{ marginBottom: 0 }}>{words.editor.outOfDateHelp}</p>
          </div>
        </div>
      )}

      <ReleaseNoteText
        note={note}
        title={title}
        onTitle={setTitle}
        content={content}
        onContent={setContent}
        titleError={titleError}
        saving={save.running}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// The Text box: the title and body of the revision, and the images the body
// refers to
// ---------------------------------------------------------------------------

function ReleaseNoteText({
  note,
  title,
  onTitle,
  content,
  onContent,
  titleError,
  saving,
}: {
  note: AdminReleaseNoteDetail;
  title: string;
  onTitle: (next: string) => void;
  content: string;
  onContent: (next: string) => void;
  titleError: string | undefined;
  saving: boolean;
}): JSX.Element {
  const words = useMessages().admin.releaseNotes;
  const api = useAdminApi();

  // The attachments, held here so an upload or a delete can move them without a
  // save; re-seeded whenever the server hands back a fresh note.
  const [images, setImages] = useState<ArticleImage[]>(note.images ?? []);
  useEffect(() => setImages(note.images ?? []), [note.images]);

  /*
   * The note as an image owner - its own routes, its own twenty-image ceiling and
   * message. Memoised on the id; the preview cache keys on the owner's key and
   * reads the functions through a ref, so even a rebuilt adapter would not
   * re-download anything.
   */
  const imageOwner = useMemo(
    () => releaseNoteImageOwner(api, note.releaseNoteId),
    [api, note.releaseNoteId],
  );

  const editor = useRef<MarkdownEditorHandle>(null);

  return (
    <div className="card">
      <div className="card-header">
        <h3>{words.editor.text}</h3>
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          {note.hasUnpublishedDraft && (
            <span className="badge badge-draft">
              {words.editor.draftBadge(note.latestRevisionNumber)}
            </span>
          )}
          {note.latestRevisionNumber === null && (
            <span className="badge badge-unwritten">{words.editor.nothingWritten}</span>
          )}
        </span>
      </div>

      {note.hasUnpublishedDraft && (
        <div className="notice notice-info">
          <div className="notice-body">
            <strong>
              {words.editor.editingDraft(note.latestRevisionNumber, note.currentRevisionNumber)}
            </strong>
            <p style={{ marginBottom: 0 }}>{words.editor.publishToShow}</p>
          </div>
        </div>
      )}

      {/* One line, at most 200 characters: kb.ReleaseNoteRevision.Title. */}
      <Field label={words.common.title} htmlFor="title" required error={titleError}>
        <input
          id="title"
          type="text"
          value={title}
          maxLength={RELEASE_NOTE_TITLE_MAX_LENGTH}
          onChange={(event) => onTitle(event.target.value)}
        />
      </Field>

      <ArticleImagePreviewsProvider imageOwner={imageOwner}>
        <MarkdownEditor
          ref={editor}
          value={content}
          onChange={onContent}
          imageOwner={imageOwner}
          noun={NOUN}
          allowVideo={false}
          allowAttachments={false}
          disabled={saving}
          images={images}
          onImagesChanged={setImages}
        />

        <ArticleImagesPanel
          imageOwner={imageOwner}
          content={content}
          images={images}
          onImagesChanged={setImages}
          onInsert={(fileName) => editor.current?.insertImageReference(fileName)}
        />
      </ArticleImagePreviewsProvider>
    </div>
  );
}
